import { Box, Toast, themeGet } from "@fuegokit/react";

import React from "react";
import styled, { keyframes } from "styled-components";

// animations
const VIEWPORT_PADDING = 10;

const hide = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
const slideIn = keyframes`
  from {
    transform: translateX(calc(100% + var(--viewport-padding)));
  }
  to {
    transform: translateX(0);
  }
`;
const swipeOut = keyframes`
  from {
    transform: translateX(var(--radix-toast-swipe-end-x));
  }
  to {
    transform: translateX(calc(100% + var(--viewport-padding)));
  }
`;
const fadeIn = keyframes`
  from {
    opacity: 0
  }
  to {
    opacity: 1
  }
`;
const fadeOut = keyframes`
  from {
    opacity: 1
  }
  to {
    opacity: 0
  }
`;
const slideRight = keyframes`
  from {
    transform: translateX(var(--radix-toast-swipe-end-x))
  }
  to {
    transform: translateX(calc(100% + ${VIEWPORT_PADDING}px))
  }
`;
const slideLeft = keyframes`
  from {
    transform: translateX(var(--radix-toast-swipe-end-x))
  }
  to {
    transform: translateX(calc(-100% - ${VIEWPORT_PADDING}px))
  }
`;
const slideUp = keyframes`
  from {
    transform: translateY(var(--radix-toast-swipe-end-y))
  }
  to {
    transform: translateY(calc(-100% - ${VIEWPORT_PADDING}px))
  }
`;
const slideDown = keyframes`
  from {
    transform: translateY(var(--radix-toast-swipe-end-y))
  }
  to {
    transform: translateY(calc(100% + ${VIEWPORT_PADDING}px))
  }
`;

// radix styled examples
export const StyledRadixToast = styled(Toast)`
  position: relative;
  overflow: hidden;
  list-style: none;
  width: 230px;
  border-radius: 3px;
  border: 1px solid black;
  box-shadow: ${themeGet("shadows.elevation.shadow.overlay")};
  padding: ${themeGet("space.3")};
  font-size: ${themeGet("fontSizes.3")};
`;
export const StyledRadixToastViewport = styled(Toast.Viewport)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid;
  display: flex;
  flex-direction: column;
  overflow: "hidden";
  padding: ${VIEWPORT_PADDING};
  gap: ${VIEWPORT_PADDING};
  list-style: none;
`;
export const StyledRadixToastHeader = styled.div`
  padding: 5px 10px;
  margin: -10px -10px 10px;
  background-color: ${themeGet("colors.background.neutral.bold.default")};
  color: ${themeGet("colors.text.inverse")};
  position: relative;
  height: 22;
  display: flex;
  align-items: center;
`;
export const StyledRadixToastSuccessHeader = styled.div`
  background-color: ${themeGet("colors.background.success.default.[default]")};
`;
export const StyledRadixToastTitle = styled(Toast.Title)`
  font-size: inherit;
  font-weight: normal;
`;
export const StyledRadixToastDescription = styled(Toast.Description)`
  margin: 0;
`;
export const StyledRadixToastActionButton = styled(Toast.Action)`
  border: 1px solid black;
  border-radius: 3px;
  background: ${themeGet("colors.elevation.surface.default.[default]")};
  font-family: inherit;
  padding: 2px 5px;
  &:hover,
  &:focus {
    background-color: ${themeGet("colors.background.accent.blue.subtle")};
    border-color: ${themeGet("colors.border.accent.blue")};
    color: ${themeGet("colors.text.inverse")};
  }
`;
export const StyledRadixCloseButton = styled(Toast.Close)`
  position: absolute;
  top: 50%;
  right: ${themeGet("space.4")};
  transform: translateY(-50%);
  width: ${themeGet("space.5")};
  height: ${themeGet("space.5")};
  padding: 0;
`;
export const StyledRadixErrorToast = styled(Toast)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
export const StyledRadixAnimatedToast = styled(Toast)`
  &[data-state="open"] {
    animation: ${fadeIn} 200ms ease-out;
  }
  &[data-state="closed"] {
    animation: ${fadeOut} 200ms ease-out;
  }
  &[data-swipe="move"] {
    transform: translate(
      var(--radix-toast-swipe-move-x),
      var(--radix-toast-swipe-move-y)
    );
  }
  &[data-swipe="cancel"] {
    transform: translate(0, 0);
    transition: transform 200ms ease-out;
  }
  &[data-swipe="end"] {
    animation-duration: 300ms;
    animation-timing-function: ease-out;
    &[data-swipe-direction="right"] {
      animation-name: ${slideRight};
    }
    &[data-swipe-direction="left"] {
      animation-name: ${slideLeft};
    }
    &[data-swipe-direction="up"] {
      animation-name: ${slideUp};
    }
    &[data-swipe-direction="down"] {
      animation-name: ${slideDown};
    }
  }
`;
const loading = keyframes`
  from {
    transform: translateX(-100%)
  }
  to {
    transform: translateX(0%)
  }
`;
export const StyledRadixProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${themeGet("space.1")};
  overflow: hidden;
  background-color: ${themeGet("colors.background.neutral.subtle.default")};
`;
export const StyledRadixProgressBarInner = styled.div`
  height: 100%;
  background-color: ${themeGet("colors.background.success.default.[default]")};
  animation-name: ${loading};
  animation-timing-function: linear;
`;
export const StyledRadixChromaticViewport = styled(Toast.Viewport)`
  display: inline-flex;
  border: 5px solid ${themeGet("colors.border.accent.blue")};
  flex-direction: column;
  padding: ${VIEWPORT_PADDING};
  gap: ${VIEWPORT_PADDING};
`;
export const ToastUpgradeAvailable = (
  props: React.ComponentProps<typeof Toast>
) => (
  <StyledRadixToast {...props}>
    <StyledRadixToastHeader>
      <StyledRadixToastTitle>Upgrade available</StyledRadixToastTitle>
      <StyledRadixCloseButton aria-label="Close">
        <span aria-hidden>×</span>
      </StyledRadixCloseButton>
    </StyledRadixToastHeader>
    <StyledRadixToastDescription>
      We've just released Radix version 3.0
    </StyledRadixToastDescription>
    <StyledRadixToastActionButton
      altText="Goto account settings to upgrade"
      style={{ marginTop: 10 }}
    >
      Upgrade
    </StyledRadixToastActionButton>
  </StyledRadixToast>
);
export const ToastSubscribeSuccess = (
  props: React.ComponentProps<typeof StyledRadixToast>
) => (
  <StyledRadixToast {...props}>
    <StyledRadixToastSuccessHeader>
      <StyledRadixToastTitle>Success!</StyledRadixToastTitle>
      <StyledRadixCloseButton aria-label="Close">
        <span aria-hidden>×</span>
      </StyledRadixCloseButton>
    </StyledRadixToastSuccessHeader>
    <StyledRadixToastDescription>
      You have subscribed. We'll be in touch.
    </StyledRadixToastDescription>
  </StyledRadixToast>
);
export const ToastWithProgress = (
  props: React.ComponentProps<typeof StyledRadixToast>
) => {
  const [paused, setPaused] = React.useState(false);
  const duration = 3000;

  return (
    <StyledRadixToast
      duration={duration}
      onPause={() => setPaused(true)}
      onResume={() => setPaused(false)}
      {...props}
    >
      <Toast.Description>Successfully saved</Toast.Description>
      <StyledRadixProgressBar>
        <StyledRadixProgressBarInner
          style={{
            animationDuration: `${duration - 100}ms`,
            animationPlayState: paused ? "paused" : "running",
          }}
        />
      </StyledRadixProgressBar>
    </StyledRadixToast>
  );
};
// our styles
export const StyledToast = styled(Toast)`
  background-color: ${themeGet("colors.elevation.surface.default.[default]")};
  border-radius: ${themeGet("radii.2")};
  box-shadow: ${themeGet("shadows.elevation.shadow.overlay")};
  padding: ${themeGet("space.4")};
  display: grid;
  grid-template-areas: "title action" "description action";
  grid-template-columns: auto max-content;
  column-gap: ${themeGet("space.4")};
  align-items: center;
  border: 1px solid ${themeGet("colors.border.subtle")};

  &[data-state="open"] {
    animation: ${slideIn} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state="closed"] {
    animation: hide 100ms ease-in;
  }
  &[data-swipe="move"] {
    transform: translateX(var(--radix-toast-swipe-move-x));
  }
  &[data-swipe="cancel"] {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }
  &[data-swipe="end"] {
    animation: ${swipeOut} 100ms ease-out;
  }
`;
export const StyledToastViewport = styled(Toast.Viewport)`
  --viewport-padding: ${themeGet("space.3")};
  position: fixed;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  padding: var(--viewport-padding);
  gap: ${themeGet("space.3")};
  width: 390px;
  max-width: 100vw;
  margin: 0;
  list-style: none;
  z-index: 2147483647;
  outline: none;
`;
export const StyledToastTitle = styled(Toast.Title)`
  grid-area: title;
  margin-bottom: ${themeGet("space.2")};
  font-weight: ${themeGet("fontWeights.semibold")};
  color: ${themeGet("colors.text.default")};
  font-size: ${themeGet("fontSizes.4")};
`;
export const StyledToastDescription = styled(Toast.Description)`
  grid-area: description;
  margin: 0;
  color: ${themeGet("colors.text.default")};
  font-size: ${themeGet("fontSizes.3")};
  line-height: ${themeGet("lineHeights.default")};
`;
export const StyledToastAction = styled(Toast.Action)`
  grid-area: action;
`;
export const StyledToastClose = styled(Toast.Close)`
  grid-area: action;
  // reset
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  user-select: none;
  display: flex;
  align-items: center;
  color: inherit;
  margin-left: ${themeGet("space.4")};
  font-weight: normal;
  font-size: ${themeGet("fontSizes.3")};
  text-decoration: none;
  opacity: 0.85;
  font-weight: ${themeGet("fontWeights.semibold")};
  padding: ${themeGet("space.2")} 12px;
  border-radius: ${themeGet("radii.1")};
  &:hover {
    opacity: 1;
    text-decoration: none;
    color: ${themeGet("colors.link.default")};
    background-color: ${themeGet("colors.background.neutral.subtle.hovered")};
  }

  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet("colors.border.focused")};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet("colors.border.focused")};
  }
  svg {
    transition: transform 0.15s ease;
  }
`;
export const StyledToastHeader = styled(Box)`
  padding: ${themeGet("space.3")} ${themeGet("space.4")};
  margin: -12px -12px 12px;
  background-color: ${themeGet("colors.background.neutral.bold.default")};
  color: ${themeGet("colors.text.inverse")};
  position: relative;
  height: ${themeGet("space.5")};
  display: flex;
  align-items: center;
`;
